import React, {
    useCallback, useEffect, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FOCUS_RING_SX from '../focusRingSx';
import { HOVERED_ICON_BUTTON_SX } from '../highContrastSx';

const ISO_FORMAT = 'YYYY-MM-DD';

const HEADER_SX = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    px: 0.5,
    pb: 0.5,
};

const MONTH_LABEL_SX = {
    fontWeight: 600,
    flex: 1,
    textAlign: 'center',
};

const WEEKDAYS_ROW_SX = {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    pt: 0.5,
};

const WEEKDAY_CELL_SX = {
    textAlign: 'center',
    color: 'grey.500',
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    py: 0.5,
};

const DAYS_GRID_SX = {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
};

const DAY_INNER_BASE_SX = {
    position: 'relative',
    width: 32,
    height: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    fontSize: '0.875rem',
    cursor: 'pointer',
    background: 'transparent',
    color: 'text.primary',
    border: 'none',
    padding: 0,
    fontFamily: 'inherit',
    transition: 'background-color 120ms ease, color 120ms ease',
    '&:hover': HOVERED_ICON_BUTTON_SX,
    '&:focus-visible': {
        ...FOCUS_RING_SX,
    },
};

const DAY_INNER_ENDPOINT_SX = {
    bgcolor: 'primary.main',
    color: 'primary.contrastText',
    '&:hover': {
        bgcolor: 'primary.dark',
    },
};

function buildMonthCells(viewMonth) {
    const start = viewMonth.clone().startOf('month').startOf('week');
    const end = viewMonth.clone().endOf('month').endOf('week');
    const cells = [];
    const cursor = start.clone();
    while (cursor.isSameOrBefore(end, 'day')) {
        cells.push(cursor.clone());
        cursor.add(1, 'day');
    }
    return cells;
}

function isStrictlyBetween(iso, low, high) {
    return Boolean(low) && Boolean(high) && iso > low && iso < high;
}

function dayCellSx({ showFullFill, showRightFill, showLeftFill }) {
    if (!showFullFill && !showRightFill && !showLeftFill) {
        return {
            position: 'relative',
            height: 36,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        };
    }
    return {
        position: 'relative',
        height: 36,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 2,
            bottom: 2,
            left: showRightFill && !showFullFill ? '50%' : 0,
            right: showLeftFill && !showFullFill ? '50%' : 0,
            bgcolor: 'selection.bgStrong',
            zIndex: 0,
        },
    };
}

function RangeCalendar({
    dateFrom, dateTo, onChange, prevLabel, nextLabel,
}) {
    const [viewMonth, setViewMonth] = useState(
        () => (dateFrom ? moment(dateFrom, ISO_FORMAT) : moment()).startOf('month'),
    );
    const [hoveredIso, setHoveredIso] = useState(null);

    useEffect(() => {
        setHoveredIso(null);
    }, [dateFrom, dateTo]);

    const cells = useMemo(() => buildMonthCells(viewMonth), [viewMonth]);

    const weekdays = useMemo(() => {
        const longNames = moment.weekdays(true);
        const shortNames = moment.weekdaysShort(true);
        return longNames.map((longName, index) => ({
            id: longName.toLowerCase(),
            label: shortNames[index].charAt(0).toUpperCase(),
        }));
    }, []);

    const handlePrevMonth = useCallback(() => {
        setViewMonth(current => current.clone().subtract(1, 'month'));
    }, []);

    const handleNextMonth = useCallback(() => {
        setViewMonth(current => current.clone().add(1, 'month'));
    }, []);

    const handleDayClick = useCallback((iso) => {
        if (!dateFrom || dateTo) {
            onChange({ dateFrom: iso, dateTo: '' });
            return;
        }
        if (iso < dateFrom) {
            onChange({ dateFrom: iso, dateTo: '' });
            return;
        }
        onChange({ dateFrom, dateTo: iso });
    }, [dateFrom, dateTo, onChange]);

    const previewEnd = (dateFrom && !dateTo && hoveredIso && hoveredIso > dateFrom)
        ? hoveredIso
        : null;
    const effectiveEnd = dateTo || previewEnd;
    const showRangeFill = Boolean(dateFrom) && Boolean(effectiveEnd) && effectiveEnd > dateFrom;

    return (
        <Box>
            <Box sx={HEADER_SX}>
                <IconButton
                    type="button"
                    onClick={handlePrevMonth}
                    aria-label={prevLabel}
                    data-testid="search-panel-when-prev-month"
                    size="small"
                >
                    <ChevronLeftIcon fontSize="small" />
                </IconButton>
                <Typography
                    component="div"
                    variant="body2"
                    sx={MONTH_LABEL_SX}
                    data-testid="search-panel-when-month-label"
                >
                    {viewMonth.format('MMMM YYYY')}
                </Typography>
                <IconButton
                    type="button"
                    onClick={handleNextMonth}
                    aria-label={nextLabel}
                    data-testid="search-panel-when-next-month"
                    size="small"
                >
                    <ChevronRightIcon fontSize="small" />
                </IconButton>
            </Box>
            <Box sx={WEEKDAYS_ROW_SX} role="row">
                {weekdays.map(weekday => (
                    <Typography
                        key={weekday.id}
                        component="div"
                        sx={WEEKDAY_CELL_SX}
                    >
                        {weekday.label}
                    </Typography>
                ))}
            </Box>
            <Box sx={DAYS_GRID_SX} role="grid" data-testid="search-panel-when-grid">
                {cells.map((cell) => {
                    const iso = cell.format(ISO_FORMAT);
                    const isCurrentMonth = cell.month() === viewMonth.month();
                    if (!isCurrentMonth) {
                        return <Box key={iso} sx={{ height: 36 }} aria-hidden="true" />;
                    }
                    const isStart = Boolean(dateFrom) && iso === dateFrom;
                    const isCommittedEnd = Boolean(dateTo) && iso === dateTo;
                    const isMiddle = isStrictlyBetween(iso, dateFrom, effectiveEnd);
                    const showFullFill = isMiddle;
                    const showRightFill = showRangeFill && isStart;
                    const showLeftFill = showRangeFill
                        && (isCommittedEnd || (!dateTo && iso === previewEnd));
                    const isEndpointCircle = isStart || isCommittedEnd;

                    return (
                        <Box
                            key={iso}
                            role="gridcell"
                            aria-selected={isEndpointCircle}
                            sx={dayCellSx({ showFullFill, showRightFill, showLeftFill })}
                        >
                            <Box
                                component="button"
                                type="button"
                                onClick={() => handleDayClick(iso)}
                                onMouseEnter={() => setHoveredIso(iso)}
                                onMouseLeave={() => setHoveredIso(null)}
                                onFocus={() => setHoveredIso(iso)}
                                onBlur={() => setHoveredIso(null)}
                                data-testid={`search-panel-when-day-${iso}`}
                                aria-label={cell.format('LL')}
                                sx={{
                                    ...DAY_INNER_BASE_SX,
                                    ...(isEndpointCircle ? DAY_INNER_ENDPOINT_SX : {}),
                                    zIndex: 1,
                                }}
                            >
                                {cell.date()}
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}

RangeCalendar.propTypes = {
    dateFrom: PropTypes.string.isRequired,
    dateTo: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    prevLabel: PropTypes.string.isRequired,
    nextLabel: PropTypes.string.isRequired,
};

export default RangeCalendar;
